import React, { useState, useEffect, useRef } from 'react';
import { Table, Button, message } from 'antd';
import { useIntl } from 'react-intl';
import ShiftsService from '../../../services/api/shifts';
import utils from '../../../utils';

const DEBOUNCE_DELAY = 2000;

const Shifts = ({ value = [], title, shiftTypes = {}, filter }) => {
  const intl = useIntl();
  const effectiveShiftTypes = {
    ...shiftTypes,
    NONE: shiftTypes.NONE || { value: '-', color: 'none' }
  };

  const shiftValues = Object.values(effectiveShiftTypes).map(t => t.value);
  const defaultShift = effectiveShiftTypes.NONE.value;

  const uniqueKeys = [...new Set(
    value.flatMap(worker => worker.shifts.map(shift => shift.key))
  )];

  const [shiftsData, setShiftsData] = useState(
    value.map(worker => ({
      id: worker.id,
      name: worker.name,
      shifts: uniqueKeys.map(key => {
        const existingShift = worker.shifts.find(s => s.key === key);
        return {
          key,
          value: existingShift ? existingShift.value : defaultShift
        };
      })
    }))
  );

  const [isSaving, setIsSaving] = useState(false);
  const saveTimeoutRef = useRef(null);
  const isFirstRenderRef = useRef(true);

  const getShiftValue = (workerId, key) => {
    const worker = shiftsData.find(w => w.id === workerId);
    const shift = worker?.shifts.find(s => s.key === key);
    return shift?.value || defaultShift;
  };

  const getShiftClass = (value) => {
    const shiftType = Object.values(effectiveShiftTypes).find(t => t.value === value);
    return `shift-cell shift-${shiftType?.color || 'none'}`;
  };

  const saveChanges = async () => {
    try {
      setIsSaving(true);

      const dataToSave = {
        shifts: shiftsData.map(worker => ({
          id: worker.id,
          shifts: worker.shifts
        })),
        id_patient: utils.getPatientLocation(),
        id_dashboard: utils.getIdDashboard(),
        filter
      };

      const result = await ShiftsService.saveShifts(dataToSave);

      if (result.success) {
        message.success(intl.formatMessage({ id: 'shifts.saved' }));
      }
    } catch (error) {
      message.error(error.message || intl.formatMessage({ id: 'shifts.error' }));
    } finally {
      setIsSaving(false);
    }
  };

  const handleShiftClick = (workerId, key) => {
    const currentShift = getShiftValue(workerId, key);
    const currentIndex = shiftValues.indexOf(currentShift);
    const nextShift = shiftValues[(currentIndex + 1) % shiftValues.length];

    setShiftsData(prevData =>
      prevData.map(worker =>
        worker.id === workerId
          ? {
              ...worker,
              shifts: worker.shifts.map(shift =>
                shift.key === key
                  ? { ...shift, value: nextShift }
                  : shift
              )
            }
          : worker
      )
    );
  };

  const handleSaveNow = () => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveChanges();
  };

  useEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      return;
    }

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(() => {
      saveChanges();
    }, DEBOUNCE_DELAY);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [shiftsData]);

  const columns = [
    {
      title: intl.formatMessage({ id: 'shifts.worker' }),
      dataIndex: 'name',
      key: 'name',
      fixed: 'left',
      width: '12%',
    },
    ...uniqueKeys.map(key => ({
      title: key,
      dataIndex: key,
      key: key,
      width: `${88 / uniqueKeys.length}%`,
      render: (text, record) => (
        <Button
          type="text"
          className={getShiftClass(getShiftValue(record.id, key))}
          onClick={() => handleShiftClick(record.id, key)}
        >
          {getShiftValue(record.id, key)}
        </Button>
      ),
    })),
  ];

  const data = shiftsData.map(worker => ({
    key: worker.id,
    id: worker.id,
    name: worker.name,
    ...uniqueKeys.reduce((acc, key) => ({
      ...acc,
      [key]: getShiftValue(worker.id, key)
    }), {})
  }));

  return (
    <div className="shifts-container">
      <div className="legend-items">
        {Object.entries(effectiveShiftTypes).map(([key, type]) => (
          <div key={key} className="legend-item">
            <span className={`shift-cell legend-indicator shift-${type.color || 'none'}`}>
              {key !== 'NONE' ? key : intl.formatMessage({ id: 'shifts.none' })} ({type.value})
            </span>
          </div>
        ))}
      </div>
      <div className="shifts-table-wrapper">
        <Table
          columns={columns}
          dataSource={data}
          pagination={false}
          bordered
          size="small"
          scroll={{ x: 'max-content' }}
        />
      </div>
      <Button
        onClick={handleSaveNow}
        className="mr-2 view-data-button"
        type="primary"
        loading={isSaving}
        disabled={isSaving}
      >
        {isSaving
          ? intl.formatMessage({ id: 'shifts.saving' })
          : intl.formatMessage({ id: 'shifts.save.now' })
        }
      </Button>
    </div>
  );
};

export default Shifts;
